import React, { useContext, memo } from 'react'
import Tooltip from '../../Tooltip'
import { useSlate } from 'slate-react'
import { css, cx } from '@emotion/css'

import { isMarkActive, isBlockActive, toggleBlock, toggleMark } from './buttonUtils'
import { EditorProvider } from '../../ContextProvider/EditorProvider'
import { formatShortcut } from '../../../utils/shortcuts/formatShortcut'
//@ts-ignore
import styles from './style.module.css'
import { IDropdownShortcutElementProps } from '../../../utils/types'

const config = {
  placement: 'bottom',
  arrow: false,
  offset: [0, 5],
  delay: [300, 0],
  duration: [0, 0],
  hideOnClick: true,
  theme: 'small'
}

export function DropdownShortcutElement(props: IDropdownShortcutElementProps) {
  const shortcut = formatShortcut(props.shortcut)
  if (shortcut)
    return <span data-test-id={props.shortcut} className={styles['dropdown-shortcut']}><span className={styles['dropdown-shortcut-text']}>{formatShortcut(props.shortcut)}</span></span>
  else
    return null

}

export declare interface ButtonProps extends React.AllHTMLAttributes<HTMLSpanElement> {
  active: any
  format: any
  shortcut: any
  ref: any,
  dropdown?: boolean,
  isActiveIcon?: boolean,
  activeTitle?: string,
  autoWidth?: boolean,
  inHoveringToolbar?: boolean
}

export const Button = memo(React.forwardRef(
  (
    {
      className,
      active,
      reversed,
      disabled,
      format,
      shortcut,
      title,
      children,
      dropdown = false,
      isActiveIcon = false,
      activeTitle = '',
      autoWidth,
      ...props
    }: Partial<ButtonProps>,
    ref: React.Ref<HTMLSpanElement>
  ) => {
    return (
      <Tooltip
        config={config}
        content={
          <div className='font-small'>
            <div>{(active && isActiveIcon) ? activeTitle : (title || format)}{' '}</div>
            {shortcut?.key !== undefined && (
              <div
                className={styles['toolbar-shortcut']}>
                {formatShortcut(shortcut.key)}
              </div>
            )}
          </div>
        }>
        <span
          {...props}
          ref={ref}
          data-icon={format}
          className={cx(
            className,
            styles['Button'],
            { [styles['Button--width-auto']]: autoWidth },
            { [styles['Button--active']]: active && !disabled && !isActiveIcon },
            { [styles['Button--hoverable']]: !((active && !isActiveIcon) || disabled) },
            { [styles['Button--disable']]: disabled },
            { [styles['Button-dropdown']]: dropdown },
            { [styles['Button--hovering-toolbar']]: props['inHoveringToolbar'] }
          )}>
          <div className={styles['flex-center']}>
            {children}
          </div>
        </span>
      </Tooltip>
    )
  }
), (prev, next) => {
  return prev.disabled === next.disabled && prev.active === next.active
})



export const BlockButton = ({ format, children, title, shortcut, autoWidth }) => {
  const editor = useSlate()
  return (
    <Button
      active={isBlockActive(editor, format)}
      onMouseDown={(event) => {
        event && event.preventDefault()
        toggleBlock(editor, format)
      }}
      format={format}
      title={title}
      shortcut={shortcut}
      autoWidth={autoWidth}>
      {children}
    </Button>
  )
}

export const DropdownMenu = ({ format, children }) => {
  const editor = useSlate()
  return (
    <span
      onMouseDown={(event) => {
        event && event.preventDefault()
        toggleBlock(editor, format)
      }}>
      {children}
    </span>
  )
}

export const MarkButton = ({ format, children, shortcut, title, toolbarType }) => {
  const editor = useSlate()
  return (
    <Button
      active={isMarkActive(editor, format)}
      onMouseDown={(event) => {
        event && event.preventDefault();
        toggleMark(editor, format)
      }}
      format={format}
      title={title}
      shortcut={shortcut}
      inHoveringToolbar={toolbarType === 'hoveringToolbar'}
    >
      {children}
    </Button>
  )
}

export const DropdownMenuItem = (props) => {
  const { icon, title, shortcut, format } = props

  return (
    <span className={styles['list_item_wrapper']} data-icon={format}>
      {icon && (
        <span className={styles['list_icon_wrapper']}>
          {icon}
        </span>
      )}
      <span className={styles['list_text_wrapper']}>
        <span className={styles['dropdown_list_text']}>{title}</span></span>
      {shortcut?.key !== undefined && <DropdownShortcutElement shortcut={shortcut.key} />}
    </span>
  )
}
